import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { businessUnitsApi } from '../services/business-units.service';
import type {
  CreateBusinessUnitInput,
  OnboardSubsidiaryInput,
  RevokeMemberInput,
  SetUnitManagerInput,
  UpdateBusinessUnitInput,
} from '../types';

export const businessUnitKeys = {
  all: ['crm', 'business-units'] as const,
  list: () => [...businessUnitKeys.all, 'list'] as const,
  mine: () => [...businessUnitKeys.all, 'mine'] as const,
  adoption: () => [...businessUnitKeys.all, 'adoption'] as const,
};

export function useBusinessUnits() {
  return useQuery({
    queryKey: businessUnitKeys.list(),
    queryFn: () => businessUnitsApi.list(),
  });
}

// P3-3b: the caller's own reachable units (works for reps, not just admins).
export function useMyUnits() {
  return useQuery({
    queryKey: businessUnitKeys.mine(),
    queryFn: () => businessUnitsApi.myUnits(),
    staleTime: 60_000,
  });
}

export function useCreateBusinessUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBusinessUnitInput) => businessUnitsApi.create(input),
    onSuccess: (unit) => {
      toast.success(`Business unit "${unit.name}" created`);
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.all });
    },
    // The D6 requiresOverride 409 is handled by the wizard inline — no toast.
    onError: (error: { response?: { data?: { requiresOverride?: boolean } } }) => {
      if (!error.response?.data?.requiresOverride) {
        toast.error('Failed to create business unit');
      }
    },
  });
}

export function useUpdateBusinessUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateBusinessUnitInput & { id: string }) =>
      businessUnitsApi.update(id, input),
    onSuccess: () => {
      toast.success('Business unit updated');
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.all });
    },
    onError: () => toast.error('Failed to update business unit'),
  });
}

export function useOnboardSubsidiary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OnboardSubsidiaryInput) => businessUnitsApi.onboard(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['crm', 'business-lines'] });
    },
    onError: () => toast.error('Operating unit setup failed'),
  });
}

export function useUnitMembers(unitId: string | null) {
  return useQuery({
    queryKey: [...businessUnitKeys.all, 'members', unitId],
    queryFn: () => businessUnitsApi.listMembers(unitId as string),
    enabled: !!unitId,
  });
}

export function useAddMember(unitId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { employeeId: string; secondmentReason?: string }) =>
      businessUnitsApi.addMember(unitId, input.employeeId, input.secondmentReason),
    onSuccess: () => {
      toast.success('Member added');
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.all });
    },
    onError: () => toast.error('Failed to add member'),
  });
}

export function useRevokeMember(unitId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RevokeMemberInput) => businessUnitsApi.revokeMember(unitId, input),
    onSuccess: () => {
      toast.success('Member revoked');
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.all });
    },
    // requiresReassignment 409 handled inline by the dialog — no toast.
    onError: (error: { response?: { data?: { requiresReassignment?: boolean } } }) => {
      if (!error.response?.data?.requiresReassignment) toast.error('Failed to revoke member');
    },
  });
}

// Slice B: set or clear the unit manager. The manager picker only lists current
// members, so setUnitManager never needs a secondment reason from this UI (an
// already-seconded member keeps their membership). Surface the backend's message
// on any failure (e.g. "must be an active employee") rather than swallowing it.
export function useSetUnitManager(unitId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetUnitManagerInput) => businessUnitsApi.setManager(unitId, input),
    onSuccess: (_res, input) => {
      toast.success(input.managerEmployeeId ? 'Unit manager set' : 'Unit manager cleared');
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.all });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message ?? 'Failed to set unit manager');
    },
  });
}

export function useCompanies() {
  return useQuery({
    queryKey: [...businessUnitKeys.all, 'companies'],
    queryFn: () => businessUnitsApi.listCompanies(),
    staleTime: 60_000,
  });
}

export function useImportableMembers(unitId: string | null, legalEntityId: string | null) {
  return useQuery({
    queryKey: [...businessUnitKeys.all, 'importable', unitId, legalEntityId],
    queryFn: () =>
      businessUnitsApi.listImportableMembers(unitId as string, legalEntityId as string),
    enabled: !!unitId && !!legalEntityId,
  });
}

export function useImportMembers(unitId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeIds: string[]) => businessUnitsApi.importMembers(unitId, employeeIds),
    onSuccess: (res) => {
      toast.success(`${res.imported} member(s) imported`);
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.all });
    },
    onError: () => toast.error('Import failed'),
  });
}

// BU-v2 A4: adoption is a resumable background job. Start it, then poll status.
export function useStartAdoption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetUnitId: string) => businessUnitsApi.startAdoption(targetUnitId),
    onSuccess: () => {
      toast.success('Adoption started — moving records in the background');
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.adoption() });
    },
    onError: () => toast.error('Could not start adoption'),
  });
}

export function useDeclineAdoption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => businessUnitsApi.declineAdoption(reason),
    onSuccess: (res) => {
      toast.success(
        `Adoption declined — ${res.defaultMembersGranted} member(s) granted default access`,
      );
      void queryClient.invalidateQueries({ queryKey: businessUnitKeys.all });
    },
    onError: () => toast.error('Could not decline adoption'),
  });
}

/** Polls adoption status while a run is in flight (PENDING/RUNNING). */
export function useAdoptionStatus(enabled = true) {
  return useQuery({
    queryKey: businessUnitKeys.adoption(),
    queryFn: () => businessUnitsApi.adoptionStatus(),
    enabled,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'PENDING' || s === 'RUNNING' ? 1500 : false;
    },
  });
}
